/**
 * Purchasing configuration — `/purchasing/settings`.
 *
 * `GET /api/v1/purchasing/config` is read-only by contract: it exposes the
 * effective tolerances that PO approval and GRN over-receipt validation apply,
 * plus any per-category overrides, with no mutation surface. The screen states
 * that plainly rather than offering controls that would 405.
 */
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { formatDateTime, formatQuantity } from "@/lib/format";

import { usePurchasingConfig } from "../queries";
import { PCT_SCALE } from "../decimal";
import { toleranceScopeLabel } from "../labels";

export interface PurchasingConfigPageProps {
  /** Currency the tolerance amount is expressed in (company base currency). */
  baseCurrency?: string;
}

export function PurchasingConfigPage({ baseCurrency = "" }: PurchasingConfigPageProps) {
  const { t, i18n } = useTranslation();
  const config = usePurchasingConfig();

  const renderPct = (value: string): string => {
    try {
      return formatQuantity(value, i18n.language, PCT_SCALE);
    } catch {
      return value;
    }
  };

  if (config.isLoading) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        {t("purchasing.common.loading", "Loading…")}
      </p>
    );
  }
  if (config.isError || config.data === undefined) {
    return (
      <div className="flex flex-col items-start gap-3 p-4">
        <p role="alert" className="text-sm text-destructive">
          {t("purchasing.config.loadError", "Purchasing configuration could not be loaded.")}
        </p>
        <Button type="button" variant="outline" onClick={() => void config.refetch()}>
          {t("actions.retry")}
        </Button>
      </div>
    );
  }

  const data = config.data;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">
          {t("purchasing.config.title", "Purchasing configuration")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "purchasing.config.subtitle",
            "Effective tolerances applied by purchase-order approval and goods-receipt validation. Read-only.",
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.config.companyTitle", "Company defaults")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("purchasing.config.overReceipt", "Over-receipt tolerance")}
              </dt>
              <dd dir="ltr" className="text-lg font-medium tabular-nums">
                {renderPct(data.over_receipt_tolerance_pct)}%
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("purchasing.config.reapproval", "Re-approval tolerance")}
              </dt>
              <dd className="text-lg font-medium">
                <MoneyDisplay
                  value={{ amount: data.reapproval_tolerance_amount, currency: baseCurrency }}
                />
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("purchasing.config.scope", "Tolerance scope")}
              </dt>
              <dd>
                <Badge variant="outline">
                  {toleranceScopeLabel(t, data.reapproval_tolerance_scope)}
                </Badge>
              </dd>
            </div>
            <div>
              <dt className="text-sm text-muted-foreground">
                {t("purchasing.config.effectiveAt", "Effective from")}
              </dt>
              <dd className="text-sm">
                {(() => {
                  try {
                    return formatDateTime(data.effective_at, i18n.language);
                  } catch {
                    return data.effective_at;
                  }
                })()}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("purchasing.config.overridesTitle", "Category overrides")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.category_overrides.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("purchasing.config.noOverrides", "No category overrides are configured.")}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase text-muted-foreground">
                  <th scope="col" className="p-2 text-start">
                    {t("purchasing.config.category", "Category")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.config.overReceipt", "Over-receipt tolerance")}
                  </th>
                  <th scope="col" className="p-2 text-end">
                    {t("purchasing.config.reapproval", "Re-approval tolerance")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.category_overrides.map((override) => (
                  <tr key={override.category_id} className="border-b">
                    <td className="p-2">
                      <code dir="ltr" className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {override.category_id}
                      </code>
                    </td>
                    <td dir="ltr" className="p-2 text-end tabular-nums">
                      {renderPct(override.over_receipt_tolerance_pct)}%
                    </td>
                    <td className="p-2 text-end">
                      <MoneyDisplay
                        value={{
                          amount: override.reapproval_tolerance_amount,
                          currency: baseCurrency,
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
